import { useContext, useEffect, useState } from "react";
import "./Home.css";
import { UserContext } from "../../../Redux/UserContext";
import { useNavigate } from "react-router-dom";
import { OrdersState, TasksDispatch, TasksState } from "../../../Redux/OrdersContext";
import { useDispatch, useSelector } from "react-redux";
import TaskModel from "../../../Models/TaskModel";
import { addTask, updateTask } from "../../../Redux/slicers/tasksSlicer";
import OrderModel from "../../../Models/OrderModel";
import { changePickedOrder, updateOrder } from "../../../Redux/slicers/ordersSlicer";
import { FiCalendar, FiCheck, FiEdit3, FiMapPin, FiPlus, FiX } from "react-icons/fi";
import { useForm } from "react-hook-form";

function Home(): JSX.Element {
	const context = useContext(UserContext);
	const navigate = useNavigate();
	const twoDaysAgo = new Date();
	twoDaysAgo.setDate(twoDaysAgo.getDate() - 1);

	const orders = useSelector((state: OrdersState) => state.orders.value);
	const readyNotPaid = orders.filter((order) =>
		order.pickup_branch !== 7 &&
		order.is_ready_for_pickup &&
		!order.is_paid &&
		new Date(order.pickup_date) < twoDaysAgo);
	const tasks = useSelector((state: TasksState) => state.tasks.value);
	const dispatch: TasksDispatch = useDispatch();
	const [isAddingTask, setIsAddingTask] = useState(false);
	const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm<TaskModel>();

	// Check if user is logged in
	useEffect(() => {
		if (context.user === null) {
			navigate("/login");
		}
	}, [context.user, navigate]);

	async function markAsDone(task: TaskModel) {
		const updatedTask = { ...task, is_done: true };
		try {
			dispatch(updateTask(updatedTask));
		} catch (err) {
			alert("Failed to mark task as done: " + err);
		}
	}

	async function submitTask(task: TaskModel) {
		task.user = context.user.id;
		task.is_done = false;

		try {
			await dispatch(addTask(task)).unwrap();
			reset();
			setIsAddingTask(false);
		} catch (err) {
			alert("Failed to add task: " + err);
		}
	}

	const showEditOrder = (order: OrderModel) => {
		dispatch(changePickedOrder(order));
		const editOrder = document.getElementById("EditOrder");
		if (editOrder) {
			editOrder.classList.add("visible");
		}
	};

	async function markOrderAsPaid(order: OrderModel) {
		try {
			await dispatch(updateOrder({ ...order, is_paid: true })).unwrap();
		} catch (err) {
			alert("Failed to mark order as paid: " + err);
		}
	}

	const branchMap = [
		"הזמנות דחופות",       // 0
		"ביג",              	// 1
		"דיסק קלאב",           // 2
		"אייסמול",             // 3
		"מתחם רד",             // 4
		"טיילת",               // 5
		"משלוח בתוך אילת",    // 6
		"משלוח בית לקוח",     // 7
		"מחסן ראשי"           // 8, main warehouse
	];

	const formatDate = (expense_date: String) => {
		const date = new Date(expense_date.toString());
		const startDay = date.getDate().toString().padStart(2, "0");
		let startMonth = date.getMonth() + 1;
		// let startYear = date.getFullYear();

		// Format startMonth and endMonth with leading zeros
		const startMonthStr = startMonth.toString().padStart(2, "0");

		// Extract the last two digits of the startYear and endYear
		// const startYearStr = startYear.toString().slice(-2);

		return `${startDay}/${startMonthStr}`;
	};

	return (
		<div className="Home Box">
			<h1> Rafi Tracky </h1>
			<h3>כלי למעקב יעיל ומהיר על כל ההזמנות הפעילות</h3>
			<section className="home-tasks" dir="rtl" aria-labelledby="open-tasks-title">
				<header className="home-tasks__header">
					<div>
						<span className="home-tasks__eyebrow">מה על הפרק?</span>
						<h2 id="open-tasks-title">משימות פתוחות</h2>
					</div>
					<div className="home-tasks__tools">
						<span className="home-tasks__count">{tasks.length} משימות</span>
						<button
							className="home-tasks__add"
							type="button"
							onClick={() => setIsAddingTask((isOpen) => !isOpen)}
							aria-expanded={isAddingTask}
						>
							{isAddingTask ? <FiX aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
							<span>{isAddingTask ? "ביטול" : "משימה חדשה"}</span>
						</button>
					</div>
				</header>
				{isAddingTask && (
					<form className="task-composer" onSubmit={handleSubmit(submitTask)}>
						<label htmlFor="home-task-description">מה צריך לעשות?</label>
						<div className="task-composer__controls">
							<textarea
								id="home-task-description"
								placeholder="כתבו כאן משימה חדשה..."
								autoFocus
								required
								{...register("description", { required: true })}
							/>
							<button type="submit" disabled={isSubmitting}>
								<FiPlus aria-hidden="true" />
								<span>{isSubmitting ? "מוסיף..." : "הוספה"}</span>
							</button>
						</div>
					</form>
				)}
				{tasks.length > 0 ? <div className="TasksContent">
					{tasks.slice(0, 5).map((task, index) => (
						<article className="task-card" key={task.id}>
							<div className="task-card__number" aria-hidden="true">{index + 1}</div>
							<p className="task-card__description">{task.description}</p>
							<button className="task-card__action" onClick={() => markAsDone(task)}>
								<FiCheck aria-hidden="true" />
								<span>סימון כבוצע</span>
							</button>
						</article>
					))}
				</div> : (
					<p className="home-tasks__empty">אין משימות פתוחות כרגע</p>
				)}
			</section>
			{readyNotPaid.length > 0 && (
				<section className="home-orders" dir="rtl" aria-labelledby="open-orders-title">
					<header className="home-orders__header">
						<div>
							<span className="home-orders__eyebrow">דורש טיפול</span>
							<h2 id="open-orders-title">הזמנות פתוחות שלא שולמו</h2>
						</div>
						<span className="home-orders__count">{readyNotPaid.length} הזמנות</span>
					</header>
					<div className="OrdersContent">
						{readyNotPaid.map((order) => (
							<article className="order-card" key={order.id}>
								<header className="order-card__header">
									<div>
										<span>הזמנה</span>
										<strong dir="ltr">#{order.order_number}</strong>
									</div>
									<span className="order-card__status">ממתינה לתשלום</span>
								</header>
								<div className="order-card__content">
									<h3>{order.customer_name}</h3>
									<h3>{order.customer_phone.replace("+972", "0")}</h3>
									<p>{order.description}</p>
								</div>
								<div className="order-card__details">
									<span><FiCalendar aria-hidden="true" /> איסוף {formatDate(order.pickup_date)}</span>
									<span><FiMapPin aria-hidden="true" /> {branchMap[order.pickup_branch]}</span>
								</div>
								<div className="order-card__footer">
									<strong dir="ltr">₪ {Number(order.total_price).toLocaleString("en-US")}</strong>
									<div className="order-card__actions">
										<button className="order-card__paid" onClick={() => markOrderAsPaid(order)}>
											<FiCheck aria-hidden="true" />
											<span>שולם</span>
										</button>
										<button onClick={() => showEditOrder(order)}>
											<FiEdit3 aria-hidden="true" />
											<span>טיפול בהזמנה</span>
										</button>
									</div>
								</div>
							</article>
						))}
					</div>
				</section>
			)}
		</div >
	)
}

export default Home;
